/**
 * Writing CSV back out: the blank template users start from, and the error report
 * they use to fix a partly-rejected upload.
 */

import { CONTENT_COLUMNS, ContentColumn, CsvRow } from "./parseContentCsv";

/** Quote a field only when it would otherwise change meaning, per RFC 4180. */
function encodeField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function toCsv(records: string[][]): string {
  return records.map((record) => record.map(encodeField).join(",")).join("\r\n");
}

/**
 * A one-row example. Filling in a template beats reading a column spec, and the
 * example row shows the two things people get wrong: a quoted multi-line `text` and
 * the date format. Written out literally — it never varies, so building it through
 * `toCsv` only hid what users actually download.
 */
export const TEMPLATE_CSV =
  "content_type,title,author,text,source_url,publish_date\r\n" +
  'blog,Example: replace this row,Author Name,' +
  '"Paste the full text here. It may contain commas, ""quotes"" and\n' +
  'line breaks, as long as the whole field stays wrapped in double quotes. ' +
  'At least 50 characters are required.",' +
  "https://example.com/article,2026-01-15";

/**
 * The rows that failed, with the reason appended, in the same shape they arrived in
 * — so the file can be corrected and uploaded again directly.
 */
export function buildErrorReportCsv(
  failures: { row: CsvRow; reason: string }[],
): string {
  const header = [...CONTENT_COLUMNS, "error"];
  const records = failures.map(({ row, reason }) => [
    ...CONTENT_COLUMNS.map((column: ContentColumn) => row.values[column] ?? ""),
    reason,
  ]);
  return toCsv([header, ...records]);
}

/** Trigger a browser download of `content` without a round-trip to the server. */
export function downloadCsv(filename: string, content: string): void {
  // The BOM is what makes Excel open UTF-8 text as UTF-8 rather than as the local
  // code page, which otherwise mangles every non-ASCII author name on the way back in.
  const blob = new Blob([`﻿${content}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
